import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import { ProductUnitReview, UnitReviewSourceItem, UnitReviewStatus } from '@core/models/models';

const REASON_LABELS: Record<string, string> = {
  name_grams_feed_kilogram: 'בשם כתוב גרם, בפיד רשום קילוגרם',
  name_kilograms_feed_gram: 'בשם כתוב קילוגרם, בפיד רשום גרם',
  name_milliliters_feed_liter: 'בשם כתוב מ״ל, בפיד רשום ליטר',
  name_liters_feed_milliliter: 'בשם כתוב ליטר, בפיד רשום מ״ל'
};

const MISSING_LABELS: Record<string, string> = {
  no_price_source: 'אין קובץ מקור במחיר האחרון של המוצר',
  unrecognized_source_name: 'שם קובץ המקור אינו מזוהה',
  file_not_on_disk: 'קובץ הפיד לא נמצא בארכיון המקומי',
  missing_item_code: 'חסר קוד פריט לחילוץ מה־XML',
  item_not_in_file: 'הפריט לא נמצא בקובץ שנטען'
};

@Component({
  selector: 'app-unit-review-admin',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './unit-review-admin.component.html',
  styleUrl: './unit-review-admin.component.css'
})
export class UnitReviewAdminComponent implements OnInit {
  private readonly api = inject(ApiService);

  status: UnitReviewStatus = 'pending';
  items: ProductUnitReview[] = [];
  loading = false;
  errorMessage = '';
  busyId: number | null = null;
  leavingId: number | null = null;
  xmlById = new Map<number, UnitReviewSourceItem>();
  xmlOpenId: number | null = null;

  readonly statuses: { id: UnitReviewStatus; label: string }[] = [
    { id: 'pending', label: 'ממתינים' },
    { id: 'confirmed', label: 'אושרו' },
    { id: 'dismissed', label: 'נדחו' }
  ];

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async load(status: UnitReviewStatus = this.status): Promise<void> {
    this.status = status;
    this.loading = true;
    this.errorMessage = '';
    this.leavingId = null;
    try {
      const response = await firstValueFrom(this.api.listUnitReviews(status));
      this.items = response.items;
    } catch {
      this.errorMessage = 'טעינת רשימת הבדיקות נכשלה.';
      this.items = [];
    } finally {
      this.loading = false;
    }
  }

  reasonLabel(reason: string): string {
    return REASON_LABELS[reason] ?? reason;
  }

  missingLabel(reason: string | null | undefined): string {
    if (!reason) {
      return 'לא נמצא XML.';
    }
    return MISSING_LABELS[reason] ?? reason;
  }

  xmlFor(id: number): UnitReviewSourceItem | undefined {
    return this.xmlById.get(id);
  }

  async apply(item: ProductUnitReview): Promise<void> {
    this.busyId = item.id;
    this.errorMessage = '';
    try {
      await firstValueFrom(this.api.applyUnitReview(item.id));
      await this.removeItem(item.id);
    } catch {
      this.errorMessage = 'אישור התיקון נכשל.';
    } finally {
      this.busyId = null;
    }
  }

  async dismiss(item: ProductUnitReview): Promise<void> {
    this.busyId = item.id;
    this.errorMessage = '';
    try {
      await firstValueFrom(this.api.dismissUnitReview(item.id));
      await this.removeItem(item.id);
    } catch {
      this.errorMessage = 'דחיית הבדיקה נכשלה.';
    } finally {
      this.busyId = null;
    }
  }

  private async removeItem(id: number): Promise<void> {
    this.leavingId = id;
    if (!this.prefersReducedMotion()) {
      await new Promise((resolve) => setTimeout(resolve, 280));
    }

    this.items = this.items.filter((row) => row.id !== id);
    this.xmlById.delete(id);
    if (this.xmlOpenId === id) {
      this.xmlOpenId = null;
    }
    this.leavingId = null;
  }

  private prefersReducedMotion(): boolean {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  async loadXml(item: ProductUnitReview): Promise<void> {
    this.busyId = item.id;
    this.errorMessage = '';
    this.xmlOpenId = item.id;
    try {
      const result = await firstValueFrom(this.api.getUnitReviewSourceItem(item.id));
      this.xmlById.set(item.id, result);
    } catch {
      this.errorMessage = 'טעינת ה־XML נכשלה.';
    } finally {
      this.busyId = null;
    }
  }

  toggleXml(item: ProductUnitReview): void {
    this.xmlOpenId = this.xmlOpenId === item.id ? null : item.id;
  }
}
